import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  timeout: 15000,
});

export const searchYouTube = async (query, opts = {}) => {
  const { maxResults = 12, excludeId, signal, confirmed = false, guest = false, forceYoutube = false, localOnly = false } = opts;
  const params = { q: query, maxResults };
  if (excludeId) params.excludeId = excludeId;
  if (confirmed || localOnly) params.confirmed = true;
  if (guest) params.guest = true;
  if (forceYoutube) params.force_youtube = true;
  if (localOnly) params.local_only = true;
  const { data } = await api.get("/youtube/search", { params, signal });
  // Return full envelope: { results, youtubeResults, forced, fromCache, fromStaleCache, message, localOnly }
  return data;
};

export const fetchSettings = async () => {
  const { data } = await api.get("/settings");
  return data;
};

export const setNowSinger = async (singer) => {
  const { data } = await api.put("/now-singer", { singer });
  return data;
};

export const updateSettings = async (guest_youtube_search) => {
  const { data } = await api.put("/settings", { guest_youtube_search });
  return data;
};

// Admin: YouTube quota / cache health stats
export const fetchYoutubeStats = async () => {
  const { data } = await api.get("/admin/youtube-stats");
  return data;
};

export const reportBlockedVideo = async ({ videoId, reason, title }) => {
  const { data } = await api.post("/youtube/blocked", { videoId, reason, title });
  return data;
};

export const fetchPlaylists = async () => {
  const { data } = await api.get("/playlists");
  return data;
};

export const createPlaylist = async (name, songs = []) => {
  const { data } = await api.post("/playlists", { name, songs });
  return data;
};

export const updatePlaylist = async (id, payload) => {
  const { data } = await api.put(`/playlists/${id}`, payload);
  return data;
};

export const deletePlaylist = async (id) => {
  const { data } = await api.delete(`/playlists/${id}`);
  return data;
};

// --- Guest submissions ---
export const createSubmission = async (payload) => {
  const { data } = await api.post("/submissions", payload);
  return data;
};

export const fetchSubmissions = async (status = "pending") => {
  const { data } = await api.get("/submissions", { params: { status } });
  return data;
};

export const approveSubmission = async (id) => {
  const { data } = await api.post(`/submissions/${id}/approve`);
  return data;
};

export const deleteSubmission = async (id) => {
  const { data } = await api.delete(`/submissions/${id}`);
  return data;
};

export const clearSubmissions = async (status) => {
  const { data } = await api.delete("/submissions", { params: status ? { status } : {} });
  return data;
};

// --- Reactions (live emoji + vote stream) ---
export const createReaction = async ({ kind, value, fromName }) => {
  const { data } = await api.post("/reactions", { kind, value, fromName });
  return data;
};

export const fetchReactions = async (since) => {
  const { data } = await api.get("/reactions", { params: since ? { since } : {} });
  return data;
};

export const fetchReactionStats = async (since, kind = "vote") => {
  const { data } = await api.get("/reactions/stats", { params: { since, kind } });
  return data;
};

// --- Song history (persistent log of every song ever added) ---
export const recordHistory = async ({ videoId, title, channelTitle, singer }) => {
  const { data } = await api.post("/history", { videoId, title, channelTitle, singer });
  return data;
};

export const fetchHistory = async () => {
  const { data } = await api.get("/history");
  return data;
};

// --- Favorites & Learned Library ---
export const fetchFavorites = async () => {
  const { data } = await api.get("/favorites");
  return data;
};

export const addFavorite = async (song) => {
  const { videoId, title, channelTitle, thumbnail, duration } = song;
  const { data } = await api.post("/favorites", {
    videoId, title, channelTitle, thumbnail, duration, query: song.query || title,
  });
  return data;
};

export const removeFavorite = async (videoId) => {
  const { data } = await api.delete(`/favorites/${videoId}`);
  return data;
};

export const recordLibraryUse = async (song, source = "manual", query = null) => {
  const { videoId, title, channelTitle, thumbnail, duration } = song;
  const { data } = await api.post("/library/use", {
    videoId, title, channelTitle, thumbnail, duration, query: query || title, source,
  });
  return data;
};

export const fetchLibrary = async (sort = "useCount", limit = 500) => {
  const { data } = await api.get("/admin/library", { params: { sort, limit } });
  return data;
};

export const fetchInvalidVideos = async () => {
  const { data } = await api.get("/admin/invalid-videos");
  return data;
};

export const markVideoInvalid = async (videoId, reason = "manual") => {
  const { data } = await api.post(`/library/${videoId}/invalid`, { reason });
  return data;
};

export const restoreVideo = async (videoId) => {
  const { data } = await api.post(`/library/${videoId}/restore`);
  return data;
};

export const deleteLibraryEntry = async (videoId) => {
  const { data } = await api.delete(`/admin/library/${videoId}`);
  return data;
};

export const hideSong = async ({ songKey, title, channelTitle, artist, query }) => {
  const { data } = await api.post("/library/hide", { songKey, title, channelTitle, artist, query });
  return data;
};

export const unhideSong = async (songKey) => {
  const { data } = await api.post("/library/unhide", { songKey });
  return data;
};

export const fetchBlacklist = async () => {
  const { data } = await api.get("/admin/blacklist");
  return data;
};

export const setContentLabel = async (videoId, label, action = "add") => {
  const { data } = await api.post(`/library/${videoId}/content-label`, { label, action });
  return data;
};

export const backfillLabels = async () => {
  const { data } = await api.post("/admin/backfill-labels", {}, { timeout: 120000 });
  return data;
};

export const refreshCache = async ({ mode, limit, force = false, dryRun = false }) => {
  const { data } = await api.post("/admin/refresh-cache", { mode, limit, force, dryRun }, { timeout: 120000 });
  return data;
};

export const exportLibrary = async (format = "json") => {
  const { data } = await api.get("/admin/export-library", {
    params: { format },
    responseType: format === "csv" ? "blob" : "json",
  });
  return data;
};

export const fetchCachedSongs = async ({ search, favorite, source } = {}) => {
  const params = {};
  if (search) params.search = search;
  if (favorite !== undefined && favorite !== null) params.favorite = favorite;
  if (source) params.source = source;
  const { data } = await api.get("/admin/cached-songs", { params });
  return data;
};

export const exportCachedSongs = async (format = "json") => {
  const { data } = await api.get("/admin/cached-songs", {
    params: { format },
    responseType: format === "csv" ? "blob" : "json",
  });
  return data;
};
